import React from "react";
import { friendlyReason } from "@/lib/liveentry-helpers";

export function ResolveModal({
  stage,
  preview,
  choices,
  setChoices,
  result,
  error,
  updateExisting,
  fileName,
  onCancel,
  onConfirm,
  onClose,
}) {
  const unmatched = preview?.unmatched || [];
  const matched = preview?.matched || 0;
  const total = preview?.total_rows_referenced || 0;
  const remaining = Object.values(choices).filter((v) => !v).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.7)" }}
      data-testid="import-resolve-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: "#11061d", border: "1px solid #3f3f3f" }}
      >
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #1f1f1f" }}>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              {stage === "done" ? "Import complete" : "Resolve unmatched names"}
            </div>
            <div className="text-base font-semibold tracking-tight">{fileName}</div>
          </div>
          <button
            onClick={stage === "done" ? onClose : onCancel}
            className="muted-text hover:text-white"
            data-testid="import-resolve-close"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="danger-text text-sm mb-3" data-testid="import-resolve-error">{error}</div>
          )}

          {stage === "done" && result && (
            <div className="text-sm" data-testid="import-resolve-result">
              {result.created === 0 && result.updated === 0 && result.duplicates_skipped === 0 ? (
                <div className="muted-text text-base mb-2">
                  Nothing was added — the file didn&rsquo;t contain any rows we could use.
                </div>
              ) : (
                <ul className="space-y-1 text-base mb-2">
                  {result.created > 0 && (
                    <li className="success-text">
                      ✓ Added {result.created} new {result.created === 1 ? "row" : "rows"}.
                    </li>
                  )}
                  {result.updated > 0 && (
                    <li className="success-text">
                      ✓ Updated {result.updated} row{result.updated === 1 ? "" : "s"} that {result.updated === 1 ? "was" : "were"} already here (new score / placement saved).
                    </li>
                  )}
                  {result.duplicates_skipped > 0 && (
                    <li className="muted-text">
                      • {result.duplicates_skipped} row{result.duplicates_skipped === 1 ? " was" : "s were"} already in the list — left as {result.duplicates_skipped === 1 ? "it was" : "they were"}.
                    </li>
                  )}
                </ul>
              )}
              {result.skipped?.length > 0 && (
                <div className="mt-3">
                  <div className="danger-text font-medium mb-1">
                    Couldn&rsquo;t read {result.skipped.length}{" "}
                    row{result.skipped.length === 1 ? "" : "s"}:
                  </div>
                  <ul className="muted-text list-disc pl-5 mt-1 max-h-40 overflow-y-auto text-xs">
                    {result.skipped.map((s, i) => (
                      <li key={`${s.line}-${i}`}>
                        {s.line ? `Row ${s.line}: ` : ""}{friendlyReason(s.reason)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage !== "done" && preview && (
            <>
              <div className="text-sm mb-3" data-testid="import-resolve-summary">
                Found <span className="success-text">{total} row{total === 1 ? "" : "s"}</span> in this file.
                {" "}
                <span className="success-text">{matched}</span> match{matched === 1 ? "es" : ""} a photographer we already know.
                {unmatched.length > 0 && (
                  <>
                    {" "}
                    <span className="text-amber-400">{unmatched.length}</span> {unmatched.length === 1 ? "name needs" : "names need"} you to pick the right person below.
                  </>
                )}
              </div>

              {/* Coloured proportion bar — green = will add, amber = needs you. */}
              {total > 0 && (
                <div className="mb-4">
                  <div
                    className="flex h-2 w-full overflow-hidden"
                    style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}
                    title={`${matched} matched · ${unmatched.length} need attention`}
                  >
                    {matched > 0 && (
                      <div
                        style={{
                          width: `${(matched / total) * 100}%`,
                          background: "#16a34a",
                        }}
                      />
                    )}
                    {unmatched.length > 0 && (
                      <div
                        style={{
                          width: `${(unmatched.length / total) * 100}%`,
                          background: "#f59e0b",
                        }}
                      />
                    )}
                  </div>
                  <div className="flex gap-4 text-[10px] uppercase tracking-wider mt-1.5">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2" style={{ background: "#16a34a" }} />
                      <span className="muted-text">Will add ({matched})</span>
                    </span>
                    {unmatched.length > 0 && (
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2" style={{ background: "#f59e0b" }} />
                        <span className="muted-text">Needs you ({unmatched.length})</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {unmatched.length === 0 && (
                <div className="text-sm success-text" data-testid="import-resolve-all-matched">
                  Everyone in this file matches a photographer in your list. Click <b>Confirm import</b> to add them.
                </div>
              )}

              {unmatched.length > 0 && (
                <div className="text-xs muted-text mb-2">
                  We&rsquo;ve guessed the closest match for each unknown name. Change the dropdown if the guess is wrong, or pick <b>Leave unmatched</b> if it&rsquo;s a brand-new member you&rsquo;ll set up later.
                </div>
              )}

              <div className="space-y-2">
                {unmatched.map((u, i) => (
                  <div
                    key={u.typed_name + i}
                    className="flex items-center gap-3 py-2 px-3"
                    style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}
                    data-testid={`unmatched-row-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" data-testid={`unmatched-typed-${i}`}>
                        {u.typed_name || "(no name)"}
                        {u.typed_code && (
                          <span className="muted-text text-xs ml-2">code: {u.typed_code}</span>
                        )}
                      </div>
                      {u.suggestions?.length === 0 && (
                        <div className="muted-text text-xs mt-0.5">No close matches in your photographer list.</div>
                      )}
                    </div>
                    <select
                      value={choices[u.typed_name] || ""}
                      onChange={(e) =>
                        setChoices((prev) => ({ ...prev, [u.typed_name]: e.target.value }))
                      }
                      className="bg-neutral-900 border border-neutral-700 px-2 py-1 text-xs focus:outline-none focus:border-amber-400"
                      style={{ minWidth: 220 }}
                      data-testid={`unmatched-select-${i}`}
                    >
                      <option value="">— Leave unmatched —</option>
                      {u.suggestions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.display_name} ({Math.round(s.score * 100)}%)
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-between gap-3" style={{ borderTop: "1px solid #1f1f1f" }}>
          {stage === "done" ? (
            <>
              <div className="text-xs muted-text">Done.</div>
              <button
                onClick={onClose}
                className="ccs-btn-primary text-sm"
                data-testid="import-resolve-done"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div className="text-xs muted-text">
                {updateExisting && <span className="text-amber-400">Overwrite existing scores: ON · </span>}
                {remaining > 0
                  ? `${remaining} name${remaining === 1 ? "" : "s"} left as “unmatched”`
                  : "All names confirmed"}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onCancel}
                  disabled={stage === "committing"}
                  className="ccs-btn-ghost text-sm"
                  data-testid="import-resolve-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={stage === "committing"}
                  className="ccs-btn-primary text-sm"
                  data-testid="import-resolve-confirm"
                >
                  {stage === "committing"
                    ? "Importing…"
                    : `Confirm import${preview?.total_rows_referenced ? ` — add ${preview.total_rows_referenced} ${preview.total_rows_referenced === 1 ? "row" : "rows"}` : ""}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
